import { useCallback, useEffect, useRef, useState } from 'react';
import { IncomingFrame, OutgoingFrame } from '../types/permanentChat';
import { API_BASE_URL, sendQuery, getSession } from '../services/api';
import { ensureDeviceToken } from '../utils/deviceToken';
import { ChatMessage as ChatMessageType } from '../types';
import { useChat } from '../context/chatContext';
import { v4 as uuidv4 } from 'uuid';

const mapMessageToChatMessage = (msg: any): ChatMessageType => ({
  id: msg.message_id ?? msg.id ?? `msg-${Date.now()}-${Math.random()}`,
  client_message_id: msg.client_msg_id ?? msg.client_message_id,
  sender: msg.sender === 'user' ? 'user' : 'enantio',
  text: msg.text ?? msg.content,
  thoughts: msg.thoughts || [],
});

const dedupeByKey = (msgs: ChatMessageType[]): ChatMessageType[] => {
  const seen = new Set<string>();
  const res: ChatMessageType[] = [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    const key = m.client_message_id ?? m.id;
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    res.push(m);
  }
  return res.reverse();
};

export function useChatSocket({
  userId,
  token,
  messages,            // from context (rendered list for active chat)
  setMessages,         // context setter (writes into windowsByChatId)
  setSessionId,
  setToken,
}: {
  userId: string;
  token?: string;
  messages: ChatMessageType[];
  setMessages: (chatId: string, messages: ChatMessageType[]) => void;
  setSessionId: (userId: string) => void;
  setToken: (token: string) => void;
}) {
  const [connected, setConnected] = useState(false);
  const [chatId, setChatId] = useState<string | undefined>();
  const [pending, setPending] = useState(true);
  const [deepLearning, setDeepLearning] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const pongTimeoutRef = useRef<number | null>(null);
  const waitingPong = useRef(false);
  const startedRef = useRef(false);
  const authErrorRef = useRef(false);
  const manualCloseRef = useRef(false);
  const ownerIdRef = useRef(Symbol('wsOwner'));
  const latestTokenRef = useRef<string | undefined>(token);
  const latestUserIdRef = useRef<string | undefined>(userId);

  // CRITICAL: keep the LATEST messages in a ref to avoid stale closures
  const messagesRef = useRef<ChatMessageType[]>(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const { setIsLoading, updateThinkingSteps } = useChat();

  useEffect(() => { latestTokenRef.current = token; }, [token]);
  useEffect(() => { latestUserIdRef.current = userId; }, [userId]);

  // --- WS owner gate (unchanged) ---
  let WS_OWNER: symbol | null = (window as any).__WS_OWNER__ ?? null;
  const isOwner = () => WS_OWNER === null || WS_OWNER === ownerIdRef.current;
  const claimOwner = () => {
    if (WS_OWNER === null || WS_OWNER === ownerIdRef.current) {
      (window as any).__WS_OWNER__ = ownerIdRef.current;
      WS_OWNER = ownerIdRef.current;
      return true;
    }
    return false;
  };
  const releaseOwner = () => {
    if (WS_OWNER === ownerIdRef.current) (window as any).__WS_OWNER__ = null;
  };

  const clearReconnectTimeout = () => {
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current !== null) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (pongTimeoutRef.current !== null) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
    waitingPong.current = false;
  }, []);

  const sendWsFrame = useCallback((frame: OutgoingFrame) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame));
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatRef.current = window.setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      waitingPong.current = true;
      sendWsFrame({ type: 'ping' });
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = window.setTimeout(() => {
        if (waitingPong.current) {
          try { /* ws.close(); */ } catch {}
        }
      }, 10_000);
    }, 30_000);
  }, [sendWsFrame, stopHeartbeat]);

  const handleMessage = useCallback(async (event: MessageEvent) => {
    const data: IncomingFrame = JSON.parse(event.data);

    if (data.type === 'history') {
      const list = dedupeByKey((data.messages || []).map(mapMessageToChatMessage));
      setChatId(data.chat_id);
      setMessages(data.chat_id, list);
      setPending(false);
      return;
    }

    if (data.type === 'messages_appended') {
      setIsLoading(false);
      const incoming = (data.messages || []).map(mapMessageToChatMessage);
      const base = messagesRef.current || [];
      const baseFiltered = base.filter(b => {
        const key = b.client_message_id ?? b.id;
        return !incoming.some(i => (i.client_message_id ?? i.id) === key);
      });
      const merged = dedupeByKey([...baseFiltered, ...incoming]);
      setMessages(data.chat_id, merged);
      return;
    }

    if (data.type === 'pong') {
      waitingPong.current = false;
      return;
    }

    if (data.type === 'error') {
      setError(data.message);
      setIsLoading(false);

      if (data.code === 'AUTH' && !authErrorRef.current) {
        authErrorRef.current = true;
        try {
          const uid = latestUserIdRef.current!;
          const curTok = latestTokenRef.current;
          const result: any = await getSession(uid, curTok);
          const newToken: string | undefined = typeof result === 'string' ? result : result?.token;

          if (newToken && newToken !== curTok) {
            setToken(newToken);
            manualCloseRef.current = true;
            clearReconnectTimeout();
            reconnectAttempt.current = 0;
            try { /* wsRef.current?.close(); */ } catch {}
            connect(true);
          }
        } catch {
          // keep error state
        } finally {
          authErrorRef.current = false;
        }
      }
      return;
    }
  }, [setIsLoading, setMessages, setToken]);

  const connect = useCallback(async (force = false) => {
    const uid = latestUserIdRef.current;
    const tok = latestTokenRef.current;
    if (!uid || !tok) return;

    const deviceToken = await ensureDeviceToken();
    if (!isOwner() && !force) return;
    if (!claimOwner() && !force) return;

    if (wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    setPending(true);

    const url = `${API_BASE_URL.replace(/^https/, 'wss').replace(/^http/, 'ws')}/ws/chat?query_token=Bearer.${tok}&device_token=${encodeURIComponent(deviceToken)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
      clearReconnectTimeout();
      startHeartbeat();
    };
    ws.onmessage = handleMessage;
    ws.onclose = () => {
      stopHeartbeat();
      setConnected(false);
      if (manualCloseRef.current) { manualCloseRef.current = false; return; }
      const attempt = reconnectAttempt.current++;
      const delay = Math.min(30_000, Math.pow(2, attempt) * 1000);
      clearReconnectTimeout();
      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
    };
    ws.onerror = (err: any) => {
      try { console.error(err); /* ws.close(); */ } catch {}
    };
  }, [handleMessage, startHeartbeat, stopHeartbeat]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    connect();
    return () => {
      stopHeartbeat();
      clearReconnectTimeout();
      manualCloseRef.current = true;
      try { /* wsRef.current?.close(); */ } catch {}
      wsRef.current = null;
      releaseOwner();
    };
  }, [connect, stopHeartbeat]);

  // SEND: optimistic message with client_message_id
  const sendMessage = useCallback(
    async (content: string, thoughtLimit: number, files: File[] = []) => {
      const uid = latestUserIdRef.current;
      if (!uid || !chatId) return;

      updateThinkingSteps([]);
      setIsLoading(true);
      setError(undefined);

      const deviceId = await ensureDeviceToken();
      const clientMessageId = deviceId + ':' + (crypto?.randomUUID?.() ?? uuidv4());

      // optimistic user message
      const optimistic: ChatMessageType = {
        id: clientMessageId,
        client_message_id: clientMessageId,
        sender: 'user',
        text: content,
        attachments: files.map((f) => ({
          name: f.name,
          type: f.type,
          dataUrl: URL.createObjectURL(f),
        })),
      };

      // append to the latest list (NOT the stale `messages` prop)
      const base = messagesRef.current || [];
      setMessages(chatId, [...base, optimistic]);

      try {
        // Fire the HTTP send; DO NOT also write the assistant here.
        // The server will emit 'messages_appended' which we handle above.
        await sendQuery(content, thoughtLimit, files, uid, chatId, clientMessageId, setSessionId, setToken, deepLearning);
      } catch (err: any) {
        // remove the optimistic if the send fails
        const afterFail = (messagesRef.current || []).filter(m => (m.client_message_id ?? m.id) !== clientMessageId);
        setMessages(chatId, afterFail);
        setError(`Failed to send: ${err?.message ?? String(err)}`);
      } finally {
        setIsLoading(false);
      }
    },
    [chatId, deepLearning, setIsLoading, setSessionId, setToken, updateThinkingSteps, setMessages]
  );

  const toggleDeepLearning = useCallback(() => setDeepLearning(prev => !prev), []);
  const clearError = useCallback(() => setError(undefined), []);

  return {
    connected,
    chatId,
    messages,   // read-only for the component; writes go through setMessages
    pending,
    deepLearning,
    error,
    sendMessage,
    toggleDeepLearning,
    clearError,
  };
}
